import React, { useState, useEffect } from "react";

export default function BuildBinaryFromDecimal() {
  const [currentDecimal, setCurrentDecimal] = useState(0);
  const [minRange, setMinRange] = useState(1);
  const [maxRange, setMaxRange] = useState(10);
  const [numBits, setNumBits] = useState(5);
  const [bits, setBits] = useState([]);
  const [feedback, setFeedback] = useState("");

  // Rebuild bits when range changes
  useEffect(() => {
    const bitsNeeded = Math.ceil(Math.log2(maxRange + 1));
    setNumBits(bitsNeeded);
    setBits(Array(bitsNeeded).fill(0));
  }, [maxRange]);

  // Generate new random decimal
  const generateDecimal = () => {
    const randomVal =
      Math.floor(Math.random() * (maxRange - minRange + 1)) + minRange;
    setCurrentDecimal(randomVal);
    setFeedback("");
    setBits(Array(numBits).fill(0));
  };

  // Initial load
  useEffect(() => {
    generateDecimal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numBits]);

  // Toggle bit
  const toggleBit = (idx) => {
    setBits((prev) =>
      prev.map((bit, i) => (i === idx ? (bit === 0 ? 1 : 0) : bit))
    );
  };

  // Check answer
  const checkAnswer = () => {
    const placeValues = Array.from({ length: numBits }, (_, i) =>
      Math.pow(2, numBits - 1 - i)
    );
    const sum = bits.reduce((acc, bit, i) => acc + bit * placeValues[i], 0);

    if (sum === currentDecimal) {
      setFeedback("Correct! 🎉");
      setTimeout(() => generateDecimal(), 1000);
    } else {
      setFeedback("Incorrect! Try again.");
    }
  };

  // Handle range change
  const handleRangeChange = (e) => {
    const [min, max] = e.target.value.split("-").map(Number);
    setMinRange(min);
    setMaxRange(max);
  };

  // Inline styles
  const styles = {
    body: {
      fontFamily: "'Comic Sans MS', Arial",
      background: "#f0f8ff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "30px",
      minHeight: "100vh",
    },
    card: {
      background: "#fff",
      padding: "20px 30px",
      borderRadius: "15px",
      boxShadow: "2px 2px 10px rgba(0,0,0,0.2)",
      marginBottom: "20px",
      width: "550px",
      textAlign: "center",
    },
    placeValues: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "20px",
    },
    pvItem: { fontWeight: "bold", fontSize: "20px", width: "50px" },
    bits: { display: "flex", justifyContent: "space-between", marginBottom: "20px" },
    bit: {
      width: "50px",
      height: "50px",
      lineHeight: "50px",
      background: "#87ceeb",
      color: "#fff",
      fontSize: "24px",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "background 0.2s",
      userSelect: "none",
    },
    bitOn: { background: "#32cd32" },
    feedback: { marginTop: "15px", fontWeight: "bold", fontSize: "18px" },
    button: {
      marginTop: "15px",
      padding: "8px 15px",
      fontSize: "16px",
      borderRadius: "8px",
      border: "none",
      background: "#ffd700",
      cursor: "pointer",
    },
  };

  // Place values array
  const placeValues = Array.from({ length: numBits }, (_, i) =>
    Math.pow(2, numBits - 1 - i)
  );

  return (
    <div style={styles.body}>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        Build Binary Number from Decimal
      </h2>

      {/* Range selection */}
      <div style={styles.card}>
        <div>
          <label htmlFor="rangeSelect">Select Decimal Range: </label>
          <select id="rangeSelect" onChange={handleRangeChange} style={styles.button}>
            <option value="1-10">1–10</option>
            <option value="10-20">10–20</option>
            <option value="20-30">20–30</option>
            <option value="30-50">30–50</option>
          </select>
        </div>
      </div>

      {/* Main card */}
      <div style={styles.card}>
        <div style={{ fontSize: "24px", marginBottom: "15px" }}>
          Decimal: {currentDecimal}
        </div>

        <div style={styles.placeValues}>
          {placeValues.map((val, i) => (
            <div key={i} style={styles.pvItem}>
              {val}
            </div>
          ))}
        </div>

        <div style={styles.bits}>
          {bits.map((bit, i) => (
            <div
              key={i}
              style={{
                ...styles.bit,
                ...(bit === 1 ? styles.bitOn : {}),
              }}
              onClick={() => toggleBit(i)}
            >
              {bit}
            </div>
          ))}
        </div>

        <button style={styles.button} onClick={checkAnswer}>
          Check Answer
        </button>
        <div
          style={{
            ...styles.feedback,
            color: feedback === "Correct! 🎉" ? "green" : "red",
          }}
        >
          {feedback}
        </div>
      </div>
    </div>
  );
}
